import { logger } from '@shared/lib/logger'
import type { ProductImageDocUrls, ProductImageView, PurcsApiResponse, PurcsResponse } from '@shared/types/purcs.types'
import { attempt } from '@shared/utils'

const PURCS_ORIGIN = 'https://lookup.previews.cimpress.io'
const PURCS_TENANT_ID = 'vistaprint-prod'

const buildSrcSet = (baseUrl: string, width: number): string => {
  return [1, 2, 3].map((dpr) => `${baseUrl}&width=${dpr * width} ${dpr}x`).join(', ')
}

interface DefaultViewImageParams {
  culture: string
  productKey: string
  productVersion?: number
  optionSelections: Record<string, string>
  previewInstructionsUri: string
  width?: number
}

/**
 * Builds a direct PURCS `defaultViewImage` URL for a product. This returns an
 * image directly (usable as an `<img>` src), so it renders reliably even for
 * products where `allViews` returns no views.
 */
export const buildDefaultViewImageUrl = ({
  culture,
  productKey,
  productVersion,
  optionSelections,
  previewInstructionsUri,
  width = 500,
}: DefaultViewImageParams): { src: string; srcSet: string } => {
  const params = new URLSearchParams({
    requestor: 'ContactUsFragment',
    purpose: 'design.studio.review',
    previewInstructionsUri,
    'purcs-react-version': '7.2.1',
  })

  if (Object.keys(optionSelections).length > 0) {
    params.set('optionSelections', JSON.stringify(optionSelections))
  }

  if (productVersion) {
    params.set('productVersion', productVersion.toString())
  }

  const baseUrl = `${PURCS_ORIGIN}/api/v1/${PURCS_TENANT_ID}/${culture}/product/${productKey}/defaultViewImage?${params.toString()}`

  return {
    src: `${baseUrl}&width=${width}`,
    srcSet: buildSrcSet(baseUrl, width),
  }
}

export const fetchDocumentUrls = async (documentUrl: string): Promise<ProductImageDocUrls | null> => {
  const { data: response, error: fetchError } = await attempt(fetch(documentUrl))

  if (fetchError) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_document_urls',
      errorMessage: `Failed to fetch documentUrl: ${String(fetchError)}`,
    })
    return null
  }

  if (!response.ok) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_document_urls',
      errorMessage: `HTTP ${response.status} ${response.statusText} for ${documentUrl}`,
    })
    return null
  }

  const { data, error: parseError } = await attempt(response.json() as Promise<ProductImageDocUrls>)

  if (parseError) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_document_urls',
      errorMessage: `Failed to parse documentUrl response: ${String(parseError)}`,
    })
    return null
  }

  return data
}

interface GetAllViewsParams {
  culture: string
  productKey: string
  productVersion?: number
  optionSelections: Record<string, string>
  previewInstructionsUri: string
  width?: number
}

export const fetchAllViews = async ({
  culture,
  productKey,
  productVersion,
  optionSelections,
  previewInstructionsUri,
  width = 500,
}: GetAllViewsParams): Promise<ProductImageView[]> => {
  const params = new URLSearchParams({
    optionSelections: JSON.stringify(optionSelections),
    requestor: 'ContactUsFragment',
    purpose: 'design.studio.review',
    previewInstructionsUri,
    width: width.toString(),
  })

  if (productVersion) {
    params.set('productVersion', productVersion.toString())
  }

  const url = `${PURCS_ORIGIN}/api/v1/${PURCS_TENANT_ID}/${culture}/product/${productKey}/allViews?${params.toString()}`

  const { data: fetchResponse, error: fetchError } = await attempt(
    fetch(url, {
      headers: { 'purcs-react-version': '7.2.1' },
    }),
  )

  if (fetchError) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_all_views',
      errorMessage: `Error retrieving views for product ${productKey}: ${String(fetchError)}`,
    })
    return []
  }

  if (!fetchResponse.ok) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_all_views',
      errorMessage: `HTTP ${fetchResponse.status} ${fetchResponse.statusText} for product ${productKey}`,
    })
    return []
  }

  const { data: response, error: parseError } = await attempt(fetchResponse.json() as Promise<PurcsApiResponse>)

  if (parseError) {
    logger.error('Contact Us Product Image', {
      action: 'fetch_all_views',
      errorMessage: `Failed to parse views for product ${productKey}: ${String(parseError)}`,
    })
    return []
  }

  return (response.views ?? []).map((view: PurcsResponse) => {
    const parsedUrl = new URL(view._links.image.href)
    const parsedQuery = parsedUrl.searchParams
    parsedQuery.delete('width')
    const baseUrl = `${parsedUrl.origin}${parsedUrl.pathname}?${parsedQuery.toString()}`

    return {
      title: view._links.image.title,
      name: view.name,
      src: view._links.image.href,
      srcSet: buildSrcSet(baseUrl, width),
    }
  })
}
